/**
 * Utility functions for handling image URLs
 */

/**
 * Safely appends query parameters to a URL without creating invalid URL formats
 * @param {string} baseUrl - The base URL to append parameters to
 * @param {Object} params - Key-value pairs of query parameters to add
 * @returns {string} - The formatted URL with properly added query parameters
 */
export const appendUrlParams = (baseUrl, params) => {
  if (!baseUrl) return baseUrl;
  
  try {
    // Create URL object to properly handle parameters
    const url = new URL(baseUrl, window.location.origin);
    
    // Add each parameter to the URL
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, value);
      }
    });
    
    // Return the formatted URL string
    return url.toString();
  } catch (error) {
    // For relative URLs or if URL parsing fails, handle manually
    const hasParams = baseUrl.includes('?');
    const separator = hasParams ? '&' : '?';
    
    const queryString = Object.entries(params)
      .filter(([_, value]) => value !== undefined && value !== null)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');
    
    return queryString ? `${baseUrl}${separator}${queryString}` : baseUrl;
  }
};

/**
 * Gets a properly formatted image URL for a product
 * @param {string|Object} image - The image (URL string or object)
 * @param {string} uniqueId - Unique identifier to append
 * @param {boolean} addTimestamp - Whether to add a timestamp to make URL unique
 * @returns {string} - Formatted image URL
 */
export const getProductImageUrl = (image, uniqueId, addTimestamp = true) => {
  // Handle null or undefined
  if (!image) return null;
  
  // Handle string URLs
  if (typeof image === 'string') {
    const params = {
      id: uniqueId
    };
    
    if (addTimestamp) {
      params.t = Date.now();
    }
    
    return appendUrlParams(image, params);
  }
  
  // Handle objects with URL property
  if (image.url) {
    return appendUrlParams(image.url, { id: uniqueId, t: addTimestamp ? Date.now() : null });
  }
  
  // If it's an object without a URL, return null
  return null;
}; 